/**
 * Connect, inspect and disconnect the signed-in user's own GitHub account.
 *
 * The credential is never here. What this panel shows is the *evidence* of one: which
 * account it belongs to, what it can do, and a short fingerprint that answers "is this
 * still the same token?" without anyone having to read the token.
 */
import { Github, Unplug } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/Controls'
import { Panel } from '@/components/ui/Panel'
import { QueryBoundary, Skeleton } from '@/components/ui/States'
import { useAuth } from '@/features/auth/useAuth'
import {
  useConnectGitHub,
  useDisconnectGitHub,
  useGitHubConnection,
} from '@/features/auth/useGitHubConnection'
import { toApiError } from '@/lib/api/errors'
import { formatRelative } from '@/lib/utils/format'

function Row({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div
      className="grid grid-cols-1 gap-1 border-b border-line/60 px-4 py-2.5 last:border-b-0
        sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4"
    >
      <dt className="text-xs text-ink-muted" title={hint}>
        {label}
      </dt>
      <dd className="min-w-0 text-sm text-ink-dim">{value}</dd>
    </div>
  )
}

/**
 * The OAuth callback returns the browser here with the outcome in the query string, because
 * that URL is opened by a person rather than a script — a JSON error document would be a
 * dead end for them.
 */
function useCallbackOutcome(): { outcome: string | null; detail: string | null } {
  const [state] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return { outcome: params.get('github'), detail: params.get('detail') }
  })

  useEffect(() => {
    if (!state.outcome) return
    // Strip the result from the URL once it has been read, so a refresh does not
    // re-announce a connection that happened minutes ago.
    const url = new URL(window.location.href)
    url.searchParams.delete('github')
    url.searchParams.delete('detail')
    window.history.replaceState({}, '', url.toString())
  }, [state.outcome])

  return state
}

export function GitHubConnection() {
  const connection = useGitHubConnection()
  const connect = useConnectGitHub()
  const disconnect = useDisconnectGitHub()
  const { refresh } = useAuth()
  const callback = useCallbackOutcome()
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)

  const account = connection.data
  const actionError = connect.error ?? disconnect.error
  // A connection that exists but is no longer honoured by GitHub. "Connect" would be the
  // wrong word for it — there is already an account attached, and what the user has to do
  // is authorize again, not choose one.
  const needsReconnect = Boolean(account && !account.connected && account.status)

  return (
    <Panel
      title="Your GitHub account"
      description="Every tool call this server makes on your behalf uses this credential"
      actions={
        // Nothing until the answer is known. Rendering "Connect GitHub" while the request is
        // in flight offers the wrong action to an already-connected user, and a click landing
        // in that window starts an authorization they did not ask for.
        connection.isPending ? null : account?.connected ? (
          confirmingDisconnect ? (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setConfirmingDisconnect(false)}>
                Keep
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={disconnect.isPending}
                onClick={() => {
                  disconnect.mutate(undefined, {
                    onSuccess: () => {
                      setConfirmingDisconnect(false)
                      void refresh()
                    },
                  })
                }}
              >
                {disconnect.isPending ? 'Disconnecting…' : 'Yes, disconnect'}
              </Button>
            </div>
          ) : (
            <Button size="sm" icon={Unplug} onClick={() => setConfirmingDisconnect(true)}>
              Disconnect
            </Button>
          )
        ) : (
          <Button
            size="sm"
            variant="primary"
            icon={Github}
            disabled={connect.isPending || account?.configured === false}
            title={
              account?.configured === false
                ? 'This server has no GitHub OAuth app configured.'
                : undefined
            }
            onClick={() => connect.mutate('/settings')}
          >
            {connect.isPending
              ? 'Redirecting…'
              : needsReconnect
                ? 'Reconnect GitHub'
                : 'Connect GitHub'}
          </Button>
        )
      }
    >
      <QueryBoundary
        isLoading={connection.isLoading}
        error={connection.error}
        onRetry={() => void connection.refetch()}
        loadingFallback={<Skeleton className="m-4 h-32" />}
      >
        {() => (
          <>
            {callback.outcome === 'connected' ? (
              <p className="border-b border-line/60 bg-ok-wash px-4 py-2 text-xs text-ok">
                Connected as {callback.detail}.
              </p>
            ) : null}
            {callback.outcome === 'error' ? (
              <p
                role="alert"
                className="border-b border-line/60 bg-fail-wash px-4 py-2 text-xs text-fail"
              >
                {callback.detail ?? 'The GitHub connection could not be completed.'}
              </p>
            ) : null}
            {actionError ? (
              <p
                role="alert"
                className="border-b border-line/60 bg-fail-wash px-4 py-2 text-xs text-fail"
              >
                {toApiError(actionError).message}
              </p>
            ) : null}

            <dl>
              <Row
                label="Status"
                value={
                  account?.connected ? (
                    <span className="text-ok">Connected</span>
                  ) : (
                    <span className="text-waiting">
                      {account?.detail ?? 'Not connected — no GitHub data is available to you yet.'}
                    </span>
                  )
                }
              />
              <Row
                label="Account"
                value={
                  account?.account_login ? (
                    <a
                      href={`https://github.com/${account.account_login}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mono text-primary-bright hover:underline"
                    >
                      {account.account_login}
                    </a>
                  ) : (
                    '—'
                  )
                }
              />
              <Row
                label="Scopes"
                value={
                  account?.scopes?.length ? (
                    <span className="mono text-xs">{account.scopes.join(', ')}</span>
                  ) : (
                    '—'
                  )
                }
                hint="What this credential is allowed to do on GitHub."
              />
              <Row
                label="Credential"
                value={
                  account?.credential_fingerprint ? (
                    <span className="mono text-xs">{account.credential_fingerprint}</span>
                  ) : (
                    '—'
                  )
                }
                hint="A short, non-reversible fingerprint. The token itself is encrypted on the server and never sent to this page."
              />
              <Row label="Connected since" value={formatRelative(account?.connected_at)} />
            </dl>
          </>
        )}
      </QueryBoundary>
    </Panel>
  )
}
